/**
 * Typed Signature Creator Component
 *
 * Allows consultants to create a signature by typing their name
 * and selecting a font, color, and size.
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Paper,
    Grid,
    FormControlLabel,
    Checkbox,
    CircularProgress,
} from '@mui/material';
import Button from 'views/Dashboards-B2B/components/Button';
import { AppDispatch } from 'redux/store';
import { createSignature } from 'redux/signatures/signatures.middleware';
import { selectSignaturesCreating } from 'redux/signatures/signatures.slice';
import {
    SIGNATURE_FONTS,
    SIGNATURE_COLORS,
    SIGNATURE_SIZES,
    SIGNATURE_DEFAULTS,
    SIGNATURE_CONSTRAINTS,
    getFontFamilyCSS,
    getFontSizePixels,
} from 'config/signatureFonts';
import type { SignatureCreateRequest } from 'types/signature';

interface TypedSignatureCreatorProps {
    onSuccess?: () => void;
}

export default function TypedSignatureCreator({ onSuccess }: TypedSignatureCreatorProps) {
    const dispatch = useDispatch<AppDispatch>();
    const isCreating = useSelector(selectSignaturesCreating);

    const [formData, setFormData] = useState({
        typed_text: '',
        font_family: SIGNATURE_DEFAULTS.font,
        color: SIGNATURE_DEFAULTS.color,
        size: SIGNATURE_DEFAULTS.size as 'small' | 'medium' | 'large',
        display_name: '',
        title: '',
        signature_name: '',
        is_default: false,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleChange = (field: string, value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.typed_text.trim()) {
            newErrors.typed_text = 'Please enter your name or signature text';
        } else if (formData.typed_text.length > SIGNATURE_CONSTRAINTS.MAX_TYPED_TEXT_LENGTH) {
            newErrors.typed_text = `Text must be less than ${SIGNATURE_CONSTRAINTS.MAX_TYPED_TEXT_LENGTH} characters`;
        }

        if (
            formData.display_name &&
            formData.display_name.length > SIGNATURE_CONSTRAINTS.MAX_DISPLAY_NAME_LENGTH
        ) {
            newErrors.display_name = `Display name must be less than ${SIGNATURE_CONSTRAINTS.MAX_DISPLAY_NAME_LENGTH} characters`;
        }

        if (formData.title && formData.title.length > SIGNATURE_CONSTRAINTS.MAX_TITLE_LENGTH) {
            newErrors.title = `Title must be less than ${SIGNATURE_CONSTRAINTS.MAX_TITLE_LENGTH} characters`;
        }

        if (
            formData.signature_name &&
            formData.signature_name.length > SIGNATURE_CONSTRAINTS.MAX_SIGNATURE_NAME_LENGTH
        ) {
            newErrors.signature_name = `Signature name must be less than ${SIGNATURE_CONSTRAINTS.MAX_SIGNATURE_NAME_LENGTH} characters`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        const payload: SignatureCreateRequest = {
            creation_method: 'typed',
            typed_text: formData.typed_text.trim(),
            font_family: formData.font_family,
            color: formData.color,
            size: formData.size,
            display_name: formData.display_name.trim() || undefined,
            title: formData.title.trim() || undefined,
            signature_name: formData.signature_name.trim() || undefined,
            is_default: formData.is_default,
        };

        try {
            await dispatch(createSignature(payload)).unwrap();
            // Reset form
            setFormData({
                typed_text: '',
                font_family: SIGNATURE_DEFAULTS.font,
                color: SIGNATURE_DEFAULTS.color,
                size: SIGNATURE_DEFAULTS.size as 'small' | 'medium' | 'large',
                display_name: '',
                title: '',
                signature_name: '',
                is_default: false,
            });
            onSuccess?.();
        } catch (error) {
            // Error is handled by Redux and displayed in parent component
            console.error('Failed to create signature:', error);
        }
    };

    const previewStyle = {
        fontFamily: getFontFamilyCSS(formData.font_family),
        fontSize: getFontSizePixels(formData.size),
        color: formData.color,
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Form Fields */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Signature Text"
                            placeholder="Enter your name"
                            value={formData.typed_text}
                            onChange={(e) => handleChange('typed_text', e.target.value)}
                            error={!!errors.typed_text}
                            helperText={
                                errors.typed_text ||
                                `${formData.typed_text.length}/${SIGNATURE_CONSTRAINTS.MAX_TYPED_TEXT_LENGTH} characters`
                            }
                            fullWidth
                            required
                            autoFocus
                        />

                        <FormControl fullWidth>
                            <InputLabel>Font</InputLabel>
                            <Select
                                value={formData.font_family}
                                label="Font"
                                onChange={(e) => handleChange('font_family', e.target.value)}
                            >
                                {SIGNATURE_FONTS.map((font) => (
                                    <MenuItem key={font.value} value={font.value}>
                                        <span style={{ fontFamily: getFontFamilyCSS(font.value) }}>
                                            {font.name}
                                        </span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Color</InputLabel>
                            <Select
                                value={formData.color}
                                label="Color"
                                onChange={(e) => handleChange('color', e.target.value)}
                            >
                                {SIGNATURE_COLORS.map((color) => (
                                    <MenuItem key={color.value} value={color.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: 1,
                                                    backgroundColor: color.value,
                                                    border: '1px solid #ccc',
                                                }}
                                            />
                                            {color.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Size</InputLabel>
                            <Select
                                value={formData.size}
                                label="Size"
                                onChange={(e) => handleChange('size', e.target.value)}
                            >
                                {SIGNATURE_SIZES.map((size) => (
                                    <MenuItem key={size.value} value={size.value}>
                                        {size.name} {size.description && `- ${size.description}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Display Name (Optional)"
                            placeholder="e.g., Dr. John Smith"
                            value={formData.display_name}
                            onChange={(e) => handleChange('display_name', e.target.value)}
                            error={!!errors.display_name}
                            helperText={errors.display_name || 'Override your name in agreements'}
                            fullWidth
                        />

                        <TextField
                            label="Professional Title (Optional)"
                            placeholder="e.g., RCIC, Immigration Consultant"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            error={!!errors.title}
                            helperText={errors.title || 'Credentials displayed below signature'}
                            fullWidth
                        />

                        <TextField
                            label="Signature Name (Optional)"
                            placeholder="e.g., Formal Signature"
                            value={formData.signature_name}
                            onChange={(e) => handleChange('signature_name', e.target.value)}
                            error={!!errors.signature_name}
                            helperText={errors.signature_name || 'Label to identify this signature'}
                            fullWidth
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.is_default}
                                    onChange={(e) => handleChange('is_default', e.target.checked)}
                                />
                            }
                            label="Set as default signature"
                        />

                        <Button
                            background="purple"
                            onClick={handleSubmit}
                            disabled={isCreating || !formData.typed_text.trim()}
                        >
                            {isCreating && (
                                <CircularProgress size={20} style={{ marginRight: '8px' }} />
                            )}
                            {isCreating ? 'Creating...' : 'Create Signature'}
                        </Button>
                    </Box>
                </Grid>

                {/* Preview */}
                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            minHeight: 300,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f9f9f9',
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Preview
                        </Typography>
                        <Box
                            sx={{
                                mt: 2,
                                p: 3,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                border: '1px dashed #ccc',
                                minWidth: 300,
                                textAlign: 'center',
                            }}
                        >
                            {formData.typed_text ? (
                                <Box>
                                    <div style={previewStyle}>{formData.typed_text}</div>
                                    {formData.title && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 1 }}
                                        >
                                            {formData.title}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontStyle="italic"
                                >
                                    Type your name to see preview
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
